<template>
	<div>
		<EditorComp
			v-if="!useHtmlEditor"
			v-model="stringContent"
			editorStyle="height: 200px"
			:pt="{ toolbar: { class: 'ql-toolbar ql-snow' } }"
			toolbar="#customToolbar"
			@text-change="onTextChange"
		>
			<template #toolbar>
				<div id="customToolbar">
					<div class="inline-flex items-center">
						<span class="ql-formats !mr-0">
							<EwTooltip>
								<template #content>
									<div class="text-center">
									Undo
									<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>Z</kbd></div>
									</div>
								</template>
								<button type="button" class="undo-btn" :disabled="!canUndo" title="Undo" @click="undo()">
									<i class="tabler:arrow-back-up flex font-500 !color-[#64748b]"></i>
								</button>
							</EwTooltip>
							<EwTooltip>
								<template #content>
									<div class="text-center">
										Redo
									<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>Y</kbd></div>
									</div>
								</template>
								<button type="button" class="redo-btn" :disabled="!canRedo" title="Redo" @click="redo()">
									<i class="tabler:arrow-forward-up flex font-500 !color-[#64748b]"></i>
								</button>
							</EwTooltip>
						</span>

						<EwTooltip>
							<template #content>
								<div class="text-center">
									Bold
								<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>B</kbd></div>
								</div>
							</template>
							<button class="ql-bold" @click="commit()" />
						</EwTooltip>
						<EwTooltip>
							<template #content>
								<div class="text-center">
									Italic
								<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>I</kbd></div>
								</div>
							</template>
							<button class="ql-italic" @click="commit()" />
						</EwTooltip>
						<EwTooltip>
							<template #content>
								<div class="text-center">
									Underline
								<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>U</kbd></div>
								</div>
							</template>
							<button class="ql-underline" @click="commit()" />
						</EwTooltip>
						<EwTooltip text="Bullet List">
							<button class="ql-list" value="bullet" @click="commit()" />
						</EwTooltip>
						<EwTooltip text="Ordered List">
							<button class="ql-list" value="ordered" @click="commit()" />
						</EwTooltip>
						<EwTooltip text="Clear Formating">
							<button class="ql-clean" value="clean" @click="commit()" />
						</EwTooltip>
						<EwTooltip text="SuperScript">
							<button class="ql-script" value="super" @click="commit()"></button>
						</EwTooltip>
						<EwTooltip text="SubScript">
							<button class="ql-script" value="sub" @click="commit()"></button>
						</EwTooltip>
						<EwTooltip text="Source Code">
							<button class="ql-code-block" @click="commit()" />
						</EwTooltip>
					</div>
				</div>
			</template>
		</EditorComp>
		<div v-else class="inplace-html-editor">
			<div class="inplace-html-toolbar">
				<EwTooltip>
					<template #content>
						<div class="text-center">
							Undo
							<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>Z</kbd></div>
						</div>
					</template>
					<button type="button" class="undo-btn" :disabled="!canUndo" title="Undo" @click="undo()">
						<i class="tabler:arrow-back-up flex font-500 !color-[#64748b]"></i>
					</button>
				</EwTooltip>
				<EwTooltip>
					<template #content>
						<div class="text-center">
							Redo
							<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>Y</kbd></div>
						</div>
					</template>
					<button type="button" class="redo-btn" :disabled="!canRedo" title="Redo" @click="redo()">
						<i class="tabler:arrow-forward-up flex font-500 !color-[#64748b]"></i>
					</button>
				</EwTooltip>
				<EwTooltip text="Bold">
					<button type="button" title="Bold" @click="runHtmlCommand('bold')"><strong>B</strong></button>
				</EwTooltip>
				<EwTooltip text="Italic">
					<button type="button" title="Italic" @click="runHtmlCommand('italic')"><em>I</em></button>
				</EwTooltip>
				<EwTooltip text="Underline">
					<button type="button" title="Underline" @click="runHtmlCommand('underline')"><u>U</u></button>
				</EwTooltip>
			</div>
			<div
				ref="htmlEditorRef"
				class="inplace-html-surface"
				:contenteditable="!disabled"
				@input="onHtmlInput"
				@blur="onHtmlInput"
			></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useManualRefHistory, useDebounceFn } from '@vueuse/core';
import { EwTooltip } from '@arkit/ew-vue-components';
import { decodeHtmlEntities } from '@/utils/htmlContent';

const content = defineModel<string>('modelValue', { required: true });
const props = withDefaults(defineProps<{ disabled?: boolean }>(), {
	disabled: false,
});
const htmlEditorRef = ref<HTMLDivElement | null>(null);
const lastRenderedHtml = ref('');

const { commit, undo, redo, canUndo, canRedo } = useManualRefHistory(content, {
	clone: true,
});

const stringContent = computed({
	get: () => decodeHtmlEntities(content.value || ''),
	set: (val: string) => {
		content.value = val;
	},
});

const useHtmlEditor = computed(() =>
	/<\/?(table|thead|tbody|tfoot|tr|th|td)\b/i.test(stringContent.value) ||
	/\s(?:style|class|colspan|rowspan)=/i.test(stringContent.value)
);

const renderHtmlEditorContent = async () => {
	await nextTick();
	if (!htmlEditorRef.value || lastRenderedHtml.value === stringContent.value) {
		return;
	}

	htmlEditorRef.value.innerHTML = stringContent.value || '';
	lastRenderedHtml.value = stringContent.value || '';
};

const onTextChange = useDebounceFn(() => {
	commit();
}, 50);

const onHtmlInput = useDebounceFn(() => {
	if (!htmlEditorRef.value) {
		return;
	}

	const nextHtml = htmlEditorRef.value.innerHTML;
	lastRenderedHtml.value = nextHtml;
	stringContent.value = nextHtml;
	commit();
}, 80);

const runHtmlCommand = (command: string) => {
	if (props.disabled) {
		return;
	}

	htmlEditorRef.value?.focus();
	document.execCommand(command, false);
	onHtmlInput();
};

watch(
	() => [useHtmlEditor.value, stringContent.value],
	() => {
		if (useHtmlEditor.value) {
			renderHtmlEditorContent();
		}
	},
	{ immediate: true }
);
</script>

<style scoped>
.inplace-html-editor {
	border: 1px solid #d6deea;
	border-radius: 6px;
	background: #fff;
	min-width: 740px;
	width: 100%;
	overflow: hidden;
}

.inplace-html-toolbar {
	display: flex;
	align-items: center;
	gap: 0.2rem;
	padding: 0.38rem 0.48rem;
	border-bottom: 1px solid #d6deea;
	background: #f8fafc;
}

.inplace-html-toolbar button {
	width: 28px;
	height: 28px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 0;
	background: transparent;
	color: #334155;
	cursor: pointer;
}

.inplace-html-toolbar button:hover:not(:disabled) {
	background: #e8eef8;
	border-radius: 4px;
}

.inplace-html-toolbar button:disabled {
	opacity: 0.45;
	cursor: no-drop;
}

.inplace-html-surface {
	height: 200px;
	padding: 0.75rem;
	overflow: auto;
	outline: none;
	color: #334155;
}

.inplace-html-surface :deep(table) {
	width: 100%;
	max-width: 100%;
	margin: 0.75rem 0 1rem;
	border-collapse: collapse;
	font-size: 0.92em;
}

.inplace-html-surface :deep(th),
.inplace-html-surface :deep(td) {
	border: 1px solid #d6deea;
	padding: 0.45rem 0.55rem;
	vertical-align: top;
}

.inplace-html-surface :deep(th) {
	background: #f6f8fc;
	font-weight: 700;
}

</style>
